import type { DraftInput, DraftMedia, DraftSummary, GameDraft } from '../../shared/drafts';
import { ApiRequestError } from '../auth/authApi';

type ErrorPayload = {
  error?: { code: string; message: string; fields?: Record<string, string> };
};

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/odevlendir/api${path}`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    ...init,
  });
  if (response.ok) {
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }
  const payload = response.headers.get('content-type')?.includes('application/json')
    ? ((await response.json()) as ErrorPayload)
    : undefined;
  throw new ApiRequestError(response.status, payload?.error);
}

export async function listDrafts(): Promise<DraftSummary[]> {
  const payload = await apiRequest<{ success: true; data: { drafts: DraftSummary[] } }>('/drafts');
  return payload.data.drafts;
}

export async function getDraft(draftId: string): Promise<GameDraft> {
  const payload = await apiRequest<{ success: true; data: { draft: GameDraft } }>(
    `/drafts/${encodeURIComponent(draftId)}`,
  );
  return payload.data.draft;
}

export async function saveDraft(input: DraftInput, draftId?: string): Promise<GameDraft> {
  const payload = await apiRequest<{ success: true; data: { draft: GameDraft } }>(
    draftId ? `/drafts/${encodeURIComponent(draftId)}` : '/drafts',
    {
      method: draftId ? 'PUT' : 'POST',
      body: JSON.stringify(input),
    },
  );
  return payload.data.draft;
}

export async function deleteDraft(draftId: string): Promise<void> {
  await apiRequest<void>(`/drafts/${encodeURIComponent(draftId)}`, { method: 'DELETE' });
}

export async function uploadDraftMedia(draftId: string, file: File): Promise<DraftMedia> {
  const payload = await apiRequest<{ success: true; data: { media: DraftMedia } }>(
    `/drafts/${encodeURIComponent(draftId)}/media`,
    {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': file.type },
    },
  );
  return payload.data.media;
}
